import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Activity,
  ArrowLeft,
  Binary,
  FileSpreadsheet,
  Mail,
  RefreshCw,
  Send,
  Sparkles,
  UserRound,
} from 'lucide-react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CsvImportPicker } from '@/components/CsvImportPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input, Select, Switch } from '@/components/ui/Input';
import { KpiCard } from '@/components/ui/KpiCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { getApiErrorMessage } from '@/lib/api-error';
import { apiClient } from '@/lib/api-client';
import type { CsvImportDraft } from '@/lib/csv-import';
import {
  ENRICHMENT_PROVIDERS,
  getEnrichmentProviderDefinition,
  normalizeEnrichmentProviders,
  type EnrichmentProviderId,
} from '@/lib/enrichment-providers';
import { ROUTES } from '@/lib/routes';
import { useCurrentWorkspace } from '@/lib/workspace-context';

type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived' | 'goal_reached';
type ProviderState =
  | 'not_configured'
  | 'configured_local'
  | 'draft'
  | 'published'
  | 'updated'
  | 'out_of_sync'
  | 'error'
  | 'archived';

interface CampaignDetailResponse {
  campaign: {
    id: string;
    name: string;
    code: string | null;
    status: CampaignStatus;
    channels_enabled: string[];
    sender_company_name: string | null;
    sender_website: string | null;
    sender_description: string | null;
    sender_first_name: string | null;
    sender_role: string | null;
    sender_email: string | null;
    offer_title: string | null;
    offer_description: string | null;
    offer_url: string | null;
    target_country: string | null;
    target_city: string | null;
    target_region: string | null;
    language: string | null;
    mail_provider: string | null;
    mail_provider_campaign_id: string | null;
    mail_provider_email_account_ids: string[];
    enrichment_providers: string[];
    enrichment_force_complete: boolean;
    reply_to_address: string | null;
    reply_to_brand_handler: string | null;
    goal_limit: number | null;
    goal_count: number;
    goal_auto_pause_enabled: boolean;
    goal_auto_paused: boolean;
    created_at: string;
    updated_at: string;
  };
  selected_accounts: Array<{
    id: string;
    email: string;
    provider: string;
    status: string;
    dailyLimit: number;
    currentDailySent: number;
  }>;
  provider_runtime: {
    state: ProviderState;
    selected_provider: string | null;
    primary_binding_id: string | null;
    bindings: Array<{
      id: string;
      provider_key: string;
      installation_id: string;
      installation_name: string;
      installation_status: string;
      capabilities: string[];
      sync_status: string;
      provider_campaign_id: string | null;
      provider_campaign_name: string | null;
      last_published_at: string | null;
      last_synced_at: string | null;
    }>;
  };
  analytics: {
    period_start: string;
    period_end: string;
    local_overview: {
      sent: number;
      total_opens: number;
      unique_opens: number;
      clicks: number;
      replies: number;
      bounced: number;
      unsubscribed: number;
      meetings_booked: number;
      meetings_completed: number;
      opportunities: number;
      open_rate: number;
      click_rate: number;
      reply_rate: number;
    };
    provider_overview: {
      sent: number;
      total_opens: number;
      unique_opens: number;
      clicks: number;
      replies: number;
      bounced: number;
      unsubscribed: number;
      meetings_booked: number;
      meetings_completed: number;
      opportunities: number;
      open_rate: number;
      click_rate: number;
      reply_rate: number;
      total_opportunity_value: number | null;
    } | null;
    local_timeline: Array<{
      date: string;
      sent: number;
      opened: number;
      unique_opened: number;
      replies: number;
      clicks: number;
      opportunities: number;
    }>;
    provider_timeline: Array<{
      date: string;
      sent: number;
      opened: number;
      unique_opened: number;
      replies: number;
      clicks: number;
      opportunities: number;
    }>;
    timeline_source: 'provider_snapshot' | 'local_events';
    account_rows: Array<{
      email_account: string;
      sent: number;
      contacted: number;
      opened: number;
      replied: number;
      bounced: number;
      combined_score: number | null;
      source: 'provider_snapshot' | 'local_events';
    }>;
    account_source: 'provider_snapshot' | 'local_events';
    latest_snapshot_at: string | null;
  };
  knowledge: {
    total_docs: number;
    workspace_docs: number;
    campaign_docs: number;
  };
  w1_handoff: {
    id: string;
    source_system: string;
    contract_version: string;
    status: 'received' | 'follow_up_required' | 'ready_for_ops' | 'ops_approved' | 'superseded';
    source_context: Record<string, unknown>;
    eligibility: Record<string, unknown>;
    project_preparation: Record<string, unknown>;
    w1_decision: Record<string, unknown>;
    activation: Record<string, unknown>;
    activation_revision: string | null;
    activation_sha256: string | null;
    signature_id: string | null;
    signed_at: string | null;
    received_at: string;
    updated_at: string;
    follow_ups: Array<{
      id: string;
      key: string;
      category: string;
      question: string;
      required: boolean;
      status: 'open' | 'answered' | 'accepted' | 'waived';
      answer: string | null;
      evidence: Record<string, unknown>;
      due_at: string | null;
      resolved_at: string | null;
      created_at: string;
      updated_at: string;
    }>;
  } | null;
}

interface ReviewQueueReason {
  code: string;
  message: string;
}

interface ReviewQueueItem {
  id: string;
  contact_id: string;
  email: string | null;
  company_name: string | null;
  position: string | null;
  score: number | null;
  reasons: ReviewQueueReason[];
  created_at: string;
}

interface ReviewQueueResponse {
  items: ReviewQueueItem[];
  total: number;
  review_queue_note: string;
}

const PROVIDER_STATE_META: Record<
  ProviderState,
  { label: string; tone: 'gray' | 'green' | 'yellow' | 'red' | 'blue' | 'purple' | 'brand' }
> = {
  not_configured: { label: 'Not configured', tone: 'gray' },
  configured_local: { label: 'Configured locally', tone: 'blue' },
  draft: { label: 'Draft binding', tone: 'yellow' },
  published: { label: 'Published', tone: 'green' },
  updated: { label: 'Synced', tone: 'brand' },
  out_of_sync: { label: 'Out of sync', tone: 'yellow' },
  error: { label: 'Provider error', tone: 'red' },
  archived: { label: 'Archived', tone: 'gray' },
};

const CHANNEL_LABELS: Record<string, string> = {
  mail: 'Email',
  call: 'Call',
  formfill: 'Form-fill',
};

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPercent(value: number | null | undefined): string {
  return `${(value ?? 0).toFixed(2)}%`;
}

function summarizeSource(value: 'provider_snapshot' | 'local_events'): string {
  return value === 'provider_snapshot' ? 'Provider snapshot' : 'Local event stream';
}

function summarizeReasons(reasons: readonly ReviewQueueReason[]): string {
  if (reasons.length === 0) return 'No reasons captured';
  return reasons
    .slice(0, 3)
    .map((reason) => reason.message || reason.code)
    .join(' • ');
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function displayValue(value: unknown, fallback = '-'): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (Array.isArray(value)) return value.map((entry) => displayValue(entry, '')).filter(Boolean).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function providerSyncSuccessMessage(
  mode: 'publish' | 'resync' | 'pause' | 'resume',
): string {
  switch (mode) {
    case 'publish':
      return 'Publication provider queuee';
    case 'resync':
      return 'Resync provider queuee';
    case 'pause':
      return 'Pause provider queuee';
    case 'resume':
      return 'Resume provider queuee';
    default:
      return 'Provider sync queued';
  }
}

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currentWorkspace } = useCurrentWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewPageSize, setReviewPageSize] = useState(25);
  const [reviewSearch, setReviewSearch] = useState('');
  const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState<string>('');
  const [csvImportDraft, setCsvImportDraft] = useState<CsvImportDraft | null>(null);
  const [enrichmentProvidersDraft, setEnrichmentProvidersDraft] = useState<EnrichmentProviderId[]>([]);
  const [enrichmentForceCompleteDraft, setEnrichmentForceCompleteDraft] = useState(false);
  const [w1Answers, setW1Answers] = useState<Record<string, string>>({});

  const query = useQuery<CampaignDetailResponse>({
    queryKey: ['campaign-detail', workspaceId, id],
    queryFn: async () => {
      const { data } = await apiClient.get<CampaignDetailResponse>(`/v1/campaigns/${id}`, {
        params: { workspace_id: workspaceId },
      });
      return data;
    },
    enabled: !!workspaceId && !!id,
    refetchInterval: 30_000,
  });

  const reviewQueueQuery = useQuery<ReviewQueueResponse>({
    queryKey: ['campaign-review-queue', workspaceId, id, reviewPage, reviewPageSize],
    queryFn: async () => {
      const { data } = await apiClient.get<ReviewQueueResponse>('/v1/campaign-fit/review-queue', {
        params: {
          workspace_id: workspaceId,
          campaign_id: id,
          limit: reviewPageSize,
          offset: reviewPage * reviewPageSize,
        },
      });
      return data;
    },
    enabled: !!workspaceId && !!id,
    refetchInterval: 30_000,
  });

  const providerSyncMutation = useMutation({
    mutationFn: async (requestedMode: 'publish' | 'resync' | 'pause' | 'resume') => {
      const { data } = await apiClient.post<{
        ok: boolean;
        task_id: string;
        requested_mode: 'publish' | 'resync' | 'pause' | 'resume';
        provider_key: string | null;
        queued_at: string;
      }>(`/v1/campaigns/${id}/provider-sync`, {
        workspace_id: workspaceId,
        requested_mode: requestedMode,
      });
      return data;
    },
    onSuccess: async (data) => {
      toast.success(providerSyncSuccessMessage(data.requested_mode));
      await queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const providerPendingPushMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{
        ok: boolean;
        eligible_count: number;
        queued_count: number;
        skipped_already_bound_count: number;
        skipped_missing_email_count: number;
        provider_key: string | null;
        task_ids: string[];
        queued_at: string;
      }>(`/v1/campaigns/${id}/provider-leads/push-pending`, {
        workspace_id: workspaceId,
        provider_key: selectedProviderKey || providerOptions[0]?.value || undefined,
      });
      return data;
    },
    onSuccess: async (data) => {
      const fragments = [`${data.queued_count} ready lead(s) queued`];
      if (data.eligible_count > data.queued_count) {
        fragments.push(`${data.eligible_count - data.queued_count} left for the next batch`);
      }
      if (data.skipped_already_bound_count > 0) {
        fragments.push(`${data.skipped_already_bound_count} already active or terminal`);
      }
      if (data.skipped_missing_email_count > 0) {
        fragments.push(`${data.skipped_missing_email_count} without usable email`);
      }
      toast.success(fragments.join(' • '));
      await queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const csvImportMutation = useMutation({
    mutationFn: async (draft: CsvImportDraft) => {
      const { data } = await apiClient.post<{
        ok: boolean;
        batch_id: string;
        job_id: string;
        task_id: string;
        rows_received: number;
        enrichment_providers: string[];
        queued_at: string;
      }>(`/v1/campaigns/${id}/enrichment-imports/csv`, {
        workspace_id: workspaceId,
        file_name: draft.fileName,
        rows: draft.rows,
        enrichment_providers: query.data?.campaign.enrichment_providers ?? [],
      });
      return data;
    },
    onSuccess: async (data) => {
      setCsvImportDraft(null);
      toast.success(`${data.rows_received} leads queued from CSV`);
      await queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const reviewResolveMutation = useMutation({
    mutationFn: async (args: {
      decisionIds: string[];
      resolution: 'send' | 'reject';
      enqueueProviderPush: boolean;
    }) => {
      const { data } = await apiClient.post<{
        ok: boolean;
        resolution: 'send' | 'reject';
        resolved_count: number;
        provider_push_queued_count: number;
        provider_key: string | null;
        task_ids: string[];
        resolved_at: string;
      }>('/v1/campaign-fit/review-queue/resolve', {
        workspace_id: workspaceId,
        campaign_id: id,
        decision_ids: args.decisionIds,
        resolution: args.resolution,
        enqueue_provider_push: args.enqueueProviderPush,
        provider_key: args.enqueueProviderPush
          ? selectedProviderKey || providerOptions[0]?.value || undefined
          : undefined,
      });
      return data;
    },
    onSuccess: async (data) => {
      setSelectedReviewIds([]);
      toast.success(
        data.resolution === 'send'
          ? `${data.resolved_count} lead(s) approved${data.provider_push_queued_count > 0 ? `, ${data.provider_push_queued_count} queued to provider` : ''}`
          : `${data.resolved_count} lead(s) rejected`,
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaign-review-queue', workspaceId, id] }),
        queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] }),
      ]);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const enrichmentSettingsMutation = useMutation({
    mutationFn: async (args: {
      enrichmentProviders: EnrichmentProviderId[];
      enrichmentForceComplete: boolean;
    }) => {
      const { data } = await apiClient.put<{
        ok: boolean;
        enrichment_providers: EnrichmentProviderId[];
        enrichment_force_complete: boolean;
      }>(`/v1/campaigns/${id}/enrichment-settings`, {
        workspace_id: workspaceId,
        enrichment_providers: args.enrichmentProviders,
        enrichment_force_complete: args.enrichmentForceComplete,
      });
      return data;
    },
    onSuccess: async (data) => {
      setEnrichmentProvidersDraft(normalizeEnrichmentProviders(data.enrichment_providers));
      setEnrichmentForceCompleteDraft(data.enrichment_force_complete);
      toast.success('Enrichment settings saved');
      await queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const w1FollowupMutation = useMutation({
    mutationFn: async (args: {
      followupId: string;
      status: 'answered' | 'accepted' | 'waived';
      answer?: string;
    }) => {
      const { data } = await apiClient.patch<{ followup: Record<string, unknown> }>(
        `/v1/campaigns/${id}/w1-followups/${args.followupId}`,
        {
          workspace_id: workspaceId,
          status: args.status,
          answer: args.answer,
        },
      );
      return data;
    },
    onSuccess: async () => {
      toast.success('Suivi W1 mis à jour');
      await queryClient.invalidateQueries({ queryKey: ['campaign-detail', workspaceId, id] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const chartRows = useMemo(
    () =>
      query.data?.analytics.timeline_source === 'provider_snapshot'
        ? query.data.analytics.provider_timeline
        : query.data?.analytics.local_timeline ?? [],
    [query.data],
  );

  const providerOptions = useMemo(() => {
    if (!query.data) return [] as Array<{ value: string; label: string }>;

    const keys = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];

    for (const binding of query.data.provider_runtime.bindings) {
      if (!binding.provider_key || keys.has(binding.provider_key)) continue;
      keys.add(binding.provider_key);
      options.push({
        value: binding.provider_key,
        label: binding.installation_name
          ? `${binding.provider_key} - ${binding.installation_name}`
          : binding.provider_key,
      });
    }

    if (query.data.provider_runtime.selected_provider && !keys.has(query.data.provider_runtime.selected_provider)) {
      options.unshift({
        value: query.data.provider_runtime.selected_provider,
        label: query.data.provider_runtime.selected_provider,
      });
    }

    return options;
  }, [query.data]);

  useEffect(() => {
    if (!selectedProviderKey && providerOptions.length > 0) {
      setSelectedProviderKey(providerOptions[0]!.value);
    }
  }, [providerOptions, selectedProviderKey]);

  useEffect(() => {
    if (!query.data) return;
    setEnrichmentProvidersDraft(
      normalizeEnrichmentProviders(query.data.campaign.enrichment_providers),
    );
    setEnrichmentForceCompleteDraft(query.data.campaign.enrichment_force_complete);
  }, [
    query.data?.campaign.enrichment_force_complete,
    query.data?.campaign.enrichment_providers,
  ]);

  useEffect(() => {
    const availableIds = new Set(reviewQueueQuery.data?.items.map((item) => item.id) ?? []);
    setSelectedReviewIds((current) => current.filter((id) => availableIds.has(id)));
  }, [reviewQueueQuery.data]);

  const filteredReviewItems = useMemo(() => {
    const items = reviewQueueQuery.data?.items ?? [];
    const needle = reviewSearch.trim().toLowerCase();
    if (!needle) return items;

    return items.filter((item) => {
      const haystacks = [
        item.email ?? '',
        item.company_name ?? '',
        item.position ?? '',
        summarizeReasons(item.reasons),
      ];
      return haystacks.some((value) => value.toLowerCase().includes(needle));
    });
  }, [reviewQueueQuery.data, reviewSearch]);

  if (!workspaceId) {
    return <div className="p-6 text-sm text-gray-500">Select a workspace to inspect a campaign.</div>;
  }

  if (!id) {
    return <div className="p-6 text-sm text-gray-500">Missing campaign id.</div>;
  }

  if (query.isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading campaign...</div>;
  }

  if (query.error || !query.data) {
    return (
      <div className="p-6 text-sm text-red-700">
        Failed to load campaign detail.
      </div>
    );
  }

  const { campaign, provider_runtime, analytics, selected_accounts, knowledge, w1_handoff } = query.data;
  const providerState = PROVIDER_STATE_META[provider_runtime.state];
  const hasMailChannel = campaign.channels_enabled.includes('mail');
  const hasProviderConfigured = Boolean(provider_runtime.selected_provider);
  const hasProviderBinding =
    provider_runtime.bindings.some((binding) => Boolean(binding.provider_campaign_id)) ||
    Boolean(campaign.mail_provider_campaign_id);
  const providerActionMode: 'publish' | 'resync' = hasProviderBinding ? 'resync' : 'publish';
  const providerActionLabel =
    providerActionMode === 'publish' ? 'Publish provider' : 'Resync provider';
  const providerRunStateMode: 'pause' | 'resume' =
    campaign.status === 'paused' ? 'resume' : 'pause';
  const providerRunStateLabel =
    providerRunStateMode === 'pause' ? 'Pause provider' : 'Resume provider';
  const selectedReviewIdsSet = new Set(selectedReviewIds);
  const allVisibleReviewIds = filteredReviewItems.map((item) => item.id);
  const allVisibleSelected =
    allVisibleReviewIds.length > 0 &&
    allVisibleReviewIds.every((decisionId) => selectedReviewIdsSet.has(decisionId));
  const queueStart = reviewQueueQuery.data?.total === 0 ? 0 : reviewPage * reviewPageSize + 1;
  const queueEnd = Math.min(
    reviewQueueQuery.data?.total ?? 0,
    reviewPage * reviewPageSize + (reviewQueueQuery.data?.items.length ?? 0),
  );
  const canApproveToProvider = hasMailChannel && providerOptions.length > 0;
  const canQueueReadyLeads = hasMailChannel && providerOptions.length > 0 && hasProviderBinding;
  const normalizedCampaignProviders = normalizeEnrichmentProviders(campaign.enrichment_providers);
  const enrichmentSettingsDirty =
    enrichmentForceCompleteDraft !== campaign.enrichment_force_complete ||
    normalizedCampaignProviders.join('|') !== enrichmentProvidersDraft.join('|');
  const emailSleuthEnabled = enrichmentProvidersDraft.includes('emailsleuth');
  const w1Eligibility = objectValue(w1_handoff?.eligibility);
  const w1Strategy = objectValue(w1_handoff?.w1_decision.strategy ?? w1_handoff?.activation.strategy);
  const w1Targeting = objectValue(w1_handoff?.w1_decision.targeting ?? w1_handoff?.activation.targeting);
  const w1Preparation = objectValue(w1_handoff?.project_preparation);
  const blockingW1Followups = w1_handoff?.follow_ups.filter(
    (followup) => followup.required && !['accepted', 'waived'].includes(followup.status),
  ).length ?? 0;

  function toggleReviewSelection(decisionId: string): void {
    setSelectedReviewIds((current) =>
      current.includes(decisionId)
        ? current.filter((value) => value !== decisionId)
        : [...current, decisionId],
    );
  }

  function toggleSelectAllVisible(checked: boolean): void {
    if (checked) {
      setSelectedReviewIds((current) => [
        ...new Set([...current, ...allVisibleReviewIds]),
      ]);
      return;
    }

    setSelectedReviewIds((current) =>
      current.filter((value) => !allVisibleReviewIds.includes(value)),
    );
  }

  function resolveReviewQueue(
    resolution: 'send' | 'reject',
    decisionIds: string[],
    enqueueProviderPush: boolean,
  ): void {
    if (decisionIds.length === 0) return;
    reviewResolveMutation.mutate({ decisionIds, resolution, enqueueProviderPush });
  }

  function toggleEnrichmentProvider(providerId: EnrichmentProviderId): void {
    setEnrichmentProvidersDraft((current) =>
      current.includes(providerId)
        ? current.filter((entry) => entry !== providerId)
        : normalizeEnrichmentProviders([...current, providerId]),
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="campaign-detail-page">
      <PageHeader
        title={campaign.name}
        description={`${campaign.sender_company_name ?? 'No sender'} - ${campaign.target_country ?? 'No target'} - ${campaign.language ?? 'n/a'}`}
        action={
          <div className="flex items-center gap-2">
            <Link to={ROUTES.CAMPAIGNS}>
              <Button variant="secondary">
                <ArrowLeft size={14} />
                Campaigns
              </Button>
            </Link>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              <RefreshCw size={14} />
              Refresh
            </Button>
            <Button
              variant="primary"
              loading={providerSyncMutation.isPending}
              disabled={!hasMailChannel || !hasProviderConfigured}
              onClick={() => providerSyncMutation.mutate(providerActionMode)}
              data-testid="provider-sync-button"
            >
              {providerActionMode === 'publish' ? <Send size={14} /> : <RefreshCw size={14} />}
              {providerActionLabel}
            </Button>
            <Button
              variant="secondary"
              loading={providerPendingPushMutation.isPending}
              disabled={!canQueueReadyLeads}
              onClick={() => providerPendingPushMutation.mutate()}
              data-testid="provider-pending-push-button"
            >
              <Send size={14} />
              Queue 500 ready leads
            </Button>
            <Button
              variant="secondary"
              loading={providerSyncMutation.isPending}
              disabled={!hasMailChannel || !hasProviderConfigured || !hasProviderBinding}
              onClick={() => providerSyncMutation.mutate(providerRunStateMode)}
              data-testid="provider-runstate-button"
            >
              <RefreshCw size={14} />
              {providerRunStateLabel}
            </Button>
          </div>
        }
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <StatusBadge status={campaign.status} />
        <Badge tone={providerState.tone} data-testid="provider-state-badge">
          {providerState.label}
        </Badge>
        {campaign.channels_enabled.map((channel) => (
          <Badge key={channel} tone="gray">
            {CHANNEL_LABELS[channel] ?? channel}
          </Badge>
        ))}
        {campaign.goal_auto_paused && <Badge tone="yellow">Goal auto-paused</Badge>}
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard
          label="Local sent"
          value={analytics.local_overview.sent}
          icon={Send}
          color="bg-brand-primary"
          sub={`${formatPercent(analytics.local_overview.open_rate)} open rate`}
        />
        <KpiCard
          label="Local replies"
          value={analytics.local_overview.replies}
          icon={Mail}
          color="bg-blue-500"
          sub={`${formatPercent(analytics.local_overview.reply_rate)} reply rate`}
        />
        <KpiCard
          label="Local opportunities"
          value={analytics.local_overview.opportunities}
          icon={Sparkles}
          color="bg-emerald-500"
          sub={`${analytics.local_overview.meetings_booked} meetings booked`}
        />
        <KpiCard
          label="Bounces"
          value={analytics.local_overview.bounced}
          icon={Activity}
          color="bg-red-500"
          sub={`${analytics.local_overview.unsubscribed} unsubscribed`}
        />
        <KpiCard
          label="Knowledge docs"
          value={knowledge.total_docs}
          icon={UserRound}
          color="bg-purple-500"
          sub={`${knowledge.campaign_docs} campaign / ${knowledge.workspace_docs} workspace`}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Analytics posture"
              subtitle="Dispatcher keeps the local event stream as source of truth and compares it with provider snapshots when available."
            />
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="rounded border border-gray-100 p-4">
                <div className="mb-1 text-sm font-medium text-brand-dark">Local truth</div>
                <div className="text-xs text-gray-500">
                  Sent {analytics.local_overview.sent} - opens {analytics.local_overview.unique_opens} - replies {analytics.local_overview.replies}
                </div>
              </div>
              <div className="rounded border border-gray-100 p-4">
                <div className="mb-1 text-sm font-medium text-brand-dark">Provider snapshot</div>
                {analytics.provider_overview ? (
                  <div className="text-xs text-gray-500">
                    Sent {analytics.provider_overview.sent} - opens {analytics.provider_overview.unique_opens} - replies {analytics.provider_overview.replies}
                    <div className="mt-1">
                      Captured {formatDateTime(analytics.latest_snapshot_at)}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">No provider snapshot stored yet.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <Tabs defaultValue="campaign" className="card">
            <TabsList className="px-5 pt-4">
              <TabsTrigger value="campaign">Campaign analytics</TabsTrigger>
              <TabsTrigger value="accounts">Account analytics</TabsTrigger>
              <TabsTrigger value="w1">W1 Getfishnet</TabsTrigger>
            </TabsList>

            <TabsContent value="campaign">
              <CardHeader
                title="Campaign analytics"
                subtitle={`Timeline source: ${summarizeSource(analytics.timeline_source)} - period ${formatDateTime(analytics.period_start)} to ${formatDateTime(analytics.period_end)}`}
              />
              <CardContent>
                {chartRows.length === 0 ? (
                  <div className="text-sm text-gray-500">No analytics rows for this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={chartRows}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                      <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="sent" stroke="#55b88b" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="opened" stroke="#3b82f6" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="replies" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </TabsContent>

            <TabsContent value="accounts">
              <CardHeader
                title="Account analytics"
                subtitle={`Account table source: ${summarizeSource(analytics.account_source)}`}
              />
              <CardContent>
                {analytics.account_rows.length === 0 ? (
                  <div className="text-sm text-gray-500">No account analytics available yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-gray-200 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Account</th>
                          <th className="px-3 py-2 text-left font-medium">Sent</th>
                          <th className="px-3 py-2 text-left font-medium">Opened</th>
                          <th className="px-3 py-2 text-left font-medium">Replied</th>
                          <th className="px-3 py-2 text-left font-medium">Bounced</th>
                          <th className="px-3 py-2 text-left font-medium">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.account_rows.map((row) => (
                          <tr key={row.email_account} className="border-b border-gray-100">
                            <td className="px-3 py-2 font-medium text-brand-dark">{row.email_account}</td>
                            <td className="px-3 py-2 text-gray-600">{row.sent}</td>
                            <td className="px-3 py-2 text-gray-600">{row.opened}</td>
                            <td className="px-3 py-2 text-gray-600">{row.replied}</td>
                            <td className="px-3 py-2 text-gray-600">{row.bounced}</td>
                            <td className="px-3 py-2 text-gray-600">
                              {row.combined_score !== null ? row.combined_score.toFixed(1) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </TabsContent>

            <TabsContent value="w1">
              <CardHeader
                title="W1 Getfishnet · passation signée"
                subtitle="Le CRM reste la source de vérité. Dispatcher conserve le paquet d’exécution reçu et le suivi opérationnel sans modifier la stratégie signée."
              />
              <CardContent>
                {!w1_handoff ? (
                  <div className="rounded border border-dashed border-gray-200 p-5 text-sm text-gray-500">
                    Cette campagne n’a pas été créée depuis un Brief d’activation W1 Getfishnet.
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge tone={blockingW1Followups > 0 ? 'yellow' : 'green'}>
                        {w1_handoff.status}
                      </Badge>
                      <Badge tone="blue">{w1_handoff.contract_version}</Badge>
                      <span className="text-xs text-gray-500">
                        Reçu le {formatDateTime(w1_handoff.received_at)} · signé le {formatDateTime(w1_handoff.signed_at)}
                      </span>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded border border-gray-100 p-4">
                        <div className="text-xs uppercase text-gray-400">Score Make Money</div>
                        <div className="mt-1 text-2xl font-semibold text-brand-dark">
                          {displayValue(w1Eligibility.make_money_score)}
                        </div>
                      </div>
                      <div className="rounded border border-gray-100 p-4">
                        <div className="text-xs uppercase text-gray-400">Potentiel prospection</div>
                        <div className="mt-1 text-2xl font-semibold text-brand-dark">
                          {displayValue(w1Eligibility.prospecting_potential_score)}
                        </div>
                      </div>
                      <div className="rounded border border-gray-100 p-4">
                        <div className="text-xs uppercase text-gray-400">Angle retenu</div>
                        <div className="mt-1 text-sm font-semibold text-brand-dark">
                          {displayValue(w1Strategy.angle_name ?? w1Eligibility.selected_angle)}
                        </div>
                      </div>
                      <div className="rounded border border-gray-100 p-4">
                        <div className="text-xs uppercase text-gray-400">ICP prioritaire</div>
                        <div className="mt-1 text-sm font-semibold text-brand-dark">
                          {displayValue(w1Targeting.summary ?? w1Eligibility.selected_icp)}
                        </div>
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-3">
                      <div className="rounded border border-gray-100 p-4">
                        <div className="mb-2 text-sm font-semibold text-brand-dark">Provenance</div>
                        <dl className="space-y-2 text-xs text-gray-600">
                          <div><dt className="text-gray-400">Projet CRM</dt><dd className="font-mono">{displayValue(w1_handoff.source_context.project_id)}</dd></div>
                          <div><dt className="text-gray-400">Rapport éligibilité</dt><dd className="font-mono">{displayValue(w1_handoff.source_context.eligibility_report_id)}</dd></div>
                          <div><dt className="text-gray-400">Brief / révision</dt><dd>{displayValue(w1_handoff.source_context.brief_id)} / {displayValue(w1_handoff.activation_revision)}</dd></div>
                          <div><dt className="text-gray-400">Hash signé</dt><dd className="break-all font-mono">{displayValue(w1_handoff.activation_sha256)}</dd></div>
                        </dl>
                      </div>
                      <div className="rounded border border-gray-100 p-4">
                        <div className="mb-2 text-sm font-semibold text-brand-dark">Préparation projet</div>
                        <dl className="space-y-2 text-xs text-gray-600">
                          <div><dt className="text-gray-400">Statut</dt><dd>{displayValue(w1Preparation.status, 'Non renseigné')}</dd></div>
                          <div><dt className="text-gray-400">Objectif commercial</dt><dd>{displayValue(w1Preparation.business_outcome)}</dd></div>
                          <div><dt className="text-gray-400">Contraintes</dt><dd>{displayValue(w1Preparation.constraints)}</dd></div>
                          <div><dt className="text-gray-400">Actifs disponibles</dt><dd>{displayValue(w1Preparation.available_assets)}</dd></div>
                        </dl>
                      </div>
                      <div className="rounded border border-gray-100 p-4">
                        <div className="mb-2 text-sm font-semibold text-brand-dark">Garde-fou d’exécution</div>
                        <p className="text-xs leading-5 text-gray-600">
                          Ce handoff autorise uniquement un brouillon. Le lancement reste une décision opérationnelle distincte après résolution des points bloquants.
                        </p>
                        <div className="mt-3 text-xs font-medium text-brand-dark">
                          {blockingW1Followups} point(s) bloquant(s)
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-brand-dark">Follow-ups W1</div>
                          <div className="text-xs text-gray-500">Réponses et arbitrages nécessaires avant validation ops.</div>
                        </div>
                        <Badge tone={blockingW1Followups > 0 ? 'yellow' : 'green'}>
                          {blockingW1Followups > 0 ? `${blockingW1Followups} bloquant(s)` : 'Prêt pour Ops'}
                        </Badge>
                      </div>
                      {w1_handoff.follow_ups.length === 0 ? (
                        <div className="rounded border border-dashed border-gray-200 p-4 text-sm text-gray-500">
                          Aucun follow-up ouvert par Hermes ou le CRM.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {w1_handoff.follow_ups.map((followup) => {
                            const draftAnswer = w1Answers[followup.id] ?? followup.answer ?? '';
                            const resolved = ['accepted', 'waived'].includes(followup.status);
                            return (
                              <div key={followup.id} className="rounded border border-gray-100 p-4">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-medium text-brand-dark">{followup.question}</div>
                                    <div className="mt-1 text-xs text-gray-500">{followup.category} · {followup.required ? 'obligatoire' : 'optionnel'}</div>
                                  </div>
                                  <Badge tone={resolved ? 'green' : followup.status === 'answered' ? 'blue' : 'yellow'}>
                                    {followup.status}
                                  </Badge>
                                </div>
                                {!resolved && (
                                  <div className="mt-3 space-y-2">
                                    <textarea
                                      value={draftAnswer}
                                      onChange={(event) => setW1Answers((current) => ({ ...current, [followup.id]: event.target.value }))}
                                      rows={3}
                                      placeholder="Réponse opérationnelle, décision ou élément de preuve…"
                                      className="w-full rounded border border-gray-200 p-3 text-sm"
                                    />
                                    <div className="flex flex-wrap gap-2">
                                      <Button
                                        size="sm"
                                        disabled={!draftAnswer.trim() || w1FollowupMutation.isPending}
                                        onClick={() => w1FollowupMutation.mutate({ followupId: followup.id, status: 'answered', answer: draftAnswer })}
                                      >
                                        Enregistrer la réponse
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        disabled={w1FollowupMutation.isPending}
                                        onClick={() => w1FollowupMutation.mutate({ followupId: followup.id, status: 'accepted', answer: draftAnswer || undefined })}
                                      >
                                        Accepter
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        disabled={w1FollowupMutation.isPending}
                                        onClick={() => w1FollowupMutation.mutate({ followupId: followup.id, status: 'waived', answer: draftAnswer || undefined })}
                                      >
                                        Lever le point
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Provider runtime" subtitle="Configured integrations and publish state" />
            <CardContent className="space-y-4">
              <div>
                <div className="mb-1 text-xs uppercase text-gray-400">Selected provider</div>
                <div className="text-sm font-medium text-brand-dark">
                  {provider_runtime.selected_provider ?? 'None'}
                </div>
              </div>

              {provider_runtime.bindings.length === 0 ? (
                <div className="rounded border border-dashed border-gray-200 p-3 text-sm text-gray-500">
                  No provider binding stored yet for this campaign.
                </div>
              ) : (
                <div className="space-y-3">
                  {provider_runtime.bindings.map((binding) => (
                    <div key={binding.id} className="rounded border border-gray-100 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-brand-dark">{binding.installation_name}</div>
                        <Badge tone={binding.sync_status === 'error' ? 'red' : binding.sync_status === 'out_of_sync' ? 'yellow' : 'green'}>
                          {binding.sync_status}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-xs text-gray-500">
                        <div>Provider: {binding.provider_key}</div>
                        <div>Campaign id: {binding.provider_campaign_id ?? '-'}</div>
                        <div>Published: {formatDateTime(binding.last_published_at)}</div>
                        <div>Synced: {formatDateTime(binding.last_synced_at)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Selected accounts" subtitle="Accounts configured in the wizard" />
            <CardContent className="space-y-3">
              {selected_accounts.length === 0 ? (
                <div className="text-sm text-gray-500">No selected sending accounts.</div>
              ) : (
                selected_accounts.map((account) => (
                  <div key={account.id} className="rounded border border-gray-100 p-3">
                    <div className="mb-1 text-sm font-medium text-brand-dark">{account.email}</div>
                    <div className="text-xs text-gray-500">
                      {account.provider} - {account.currentDailySent}/{account.dailyLimit} sent today
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader title="Campaign setup" subtitle="Mail and knowledge configuration" />
            <CardContent className="space-y-3 text-sm text-gray-600">
              <div>
                <div className="text-xs uppercase text-gray-400">Reply-to</div>
                <div>{campaign.reply_to_address ?? 'Dispatcher native inbox'}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Offer</div>
                <div>{campaign.offer_title ?? 'No offer title'}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400">Knowledge docs</div>
                <div>{knowledge.campaign_docs} campaign / {knowledge.workspace_docs} workspace</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Enrichment sources"
              subtitle="Continue d'alimenter la campagne avec les sources configurees, puis repasse par le pipeline de dedupe, enrichissement et routage."
              action={
                <Link to={ROUTES.ENRICHERS}>
                  <Button variant="secondary" size="sm">
                    <Binary size={14} />
                    EmailSleuth
                  </Button>
                </Link>
              }
            />
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {enrichmentProvidersDraft.length > 0 ? (
                  enrichmentProvidersDraft.map((provider) => (
                    <Badge key={provider} tone="brand">
                      {getEnrichmentProviderDefinition(provider)?.label ?? provider}
                    </Badge>
                  ))
                ) : (
                  <Badge tone="yellow">No enrichment source configured yet</Badge>
                )}
              </div>

              <div className="space-y-3 rounded border border-gray-100 bg-gray-50 px-4 py-4">
                {ENRICHMENT_PROVIDERS.map((provider) => (
                  <div
                    key={provider.id}
                    className="flex items-start justify-between gap-3 rounded border border-white bg-white px-3 py-3"
                  >
                    <Checkbox
                      id={`campaign-enrichment-provider-${provider.id}`}
                      checked={enrichmentProvidersDraft.includes(provider.id)}
                      onChange={() => toggleEnrichmentProvider(provider.id)}
                      label={provider.label}
                      description={provider.description}
                      data-testid={`campaign-enrichment-provider-${provider.id}`}
                    />
                  </div>
                ))}
              </div>

              <div
                className="flex items-center justify-between rounded border border-gray-100 bg-gray-50 px-3 py-3 text-xs text-gray-600"
                data-testid="campaign-enrichment-force-complete-row"
              >
                <div>
                  <div className="font-medium text-gray-700">Force complete</div>
                  <div>Re-enrichit meme si un resultat recent existe deja en cache.</div>
                </div>
                <Switch
                  checked={enrichmentForceCompleteDraft}
                  onChange={setEnrichmentForceCompleteDraft}
                />
              </div>

              {emailSleuthEnabled && (
                <div
                  className="rounded border border-brand-border bg-brand-surface px-3 py-3 text-sm text-gray-700"
                  data-testid="campaign-emailsleuth-note"
                >
                  EmailSleuth est surtout utile quand tes imports contiennent deja un nom complet
                  et un domaine d'entreprise, par exemple apres preparation d'une liste LinkedIn.
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="primary"
                  disabled={!enrichmentSettingsDirty || enrichmentSettingsMutation.isPending}
                  loading={enrichmentSettingsMutation.isPending}
                  onClick={() =>
                    enrichmentSettingsMutation.mutate({
                      enrichmentProviders: enrichmentProvidersDraft,
                      enrichmentForceComplete: enrichmentForceCompleteDraft,
                    })
                  }
                  data-testid="campaign-enrichment-settings-save"
                >
                  Save enrichment settings
                </Button>
              </div>

              <CsvImportPicker
                value={csvImportDraft}
                onChange={setCsvImportDraft}
                title="Add more leads from CSV"
                subtitle="Recharge un nouveau lot pour cette campagne. Les lignes repartent dans le meme moteur que les autres enrichissements."
                maxPreviewRows={2}
                dataTestId="campaign-detail-csv-import"
              />

              <Button
                variant="primary"
                className="w-full"
                disabled={!csvImportDraft || csvImportMutation.isPending}
                loading={csvImportMutation.isPending}
                onClick={() => {
                  if (csvImportDraft) {
                    csvImportMutation.mutate(csvImportDraft);
                  }
                }}
                data-testid="campaign-detail-csv-import-submit"
              >
                <FileSpreadsheet size={14} />
                Queue CSV import
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <Card className="mt-6">
        <CardHeader
          title="Review queue"
          subtitle={
            reviewQueueQuery.data?.review_queue_note ??
            'Operator review lets you validate ambiguous leads before provider upload.'
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              {providerOptions.length > 0 && (
                <Select
                  className="min-w-[220px]"
                  value={selectedProviderKey}
                  onChange={(event) => setSelectedProviderKey(event.target.value)}
                  data-testid="review-queue-provider-select"
                >
                  {providerOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              )}
              <Button
                variant="secondary"
                onClick={() => void reviewQueueQuery.refetch()}
                disabled={reviewQueueQuery.isFetching}
              >
                <RefreshCw size={14} />
                Refresh queue
              </Button>
              <Button
                variant="secondary"
                disabled={selectedReviewIds.length === 0 || reviewResolveMutation.isPending}
                onClick={() => resolveReviewQueue('reject', selectedReviewIds, false)}
                data-testid="review-queue-reject-selected"
              >
                Reject selected
              </Button>
              <Button
                variant="primary"
                disabled={
                  selectedReviewIds.length === 0 ||
                  reviewResolveMutation.isPending ||
                  !canApproveToProvider
                }
                loading={reviewResolveMutation.isPending}
                onClick={() => resolveReviewQueue('send', selectedReviewIds, true)}
                data-testid="review-queue-approve-selected"
              >
                <Send size={14} />
                Approve & queue
              </Button>
            </div>
          }
        />
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="blue">{reviewQueueQuery.data?.total ?? 0} pending reviews</Badge>
              {!canApproveToProvider && (
                <Badge tone="yellow">Publish or configure a mail provider to queue approvals</Badge>
              )}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Input
                value={reviewSearch}
                onChange={(event) => setReviewSearch(event.target.value)}
                placeholder="Search current page by email, company, role or reason"
                className="min-w-[320px]"
                data-testid="review-queue-search"
              />
              <Select
                value={String(reviewPageSize)}
                onChange={(event) => {
                  setReviewPageSize(Number(event.target.value));
                  setReviewPage(0);
                }}
                className="w-[120px]"
                data-testid="review-queue-page-size"
              >
                <option value="25">25 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </Select>
            </div>
          </div>

          {reviewQueueQuery.isLoading ? (
            <div className="rounded border border-dashed border-gray-200 px-4 py-8 text-sm text-gray-500">
              Loading review queue...
            </div>
          ) : filteredReviewItems.length === 0 ? (
            <div className="rounded border border-dashed border-gray-200 px-4 py-8 text-sm text-gray-500">
              {reviewQueueQuery.data?.total
                ? 'No matches on this page for the current search.'
                : 'No leads are waiting for operator review on this campaign.'}
            </div>
          ) : (
            <div className="overflow-hidden rounded border border-gray-100">
              <div className="flex items-center justify-between gap-4 border-b border-gray-100 bg-gray-50 px-4 py-3">
                <Checkbox
                  id="review-queue-select-all"
                  checked={allVisibleSelected}
                  onChange={(event) =>
                    toggleSelectAllVisible((event.target as HTMLInputElement).checked)
                  }
                  label={`Select page (${filteredReviewItems.length})`}
                />
                <div className="text-xs text-gray-500">
                  Showing {queueStart}-{queueEnd} of {reviewQueueQuery.data?.total ?? 0}
                </div>
              </div>

              <div className="divide-y divide-gray-100">
                {filteredReviewItems.map((item) => {
                  const checked = selectedReviewIdsSet.has(item.id);
                  return (
                    <div
                      key={item.id}
                      className="grid gap-4 px-4 py-4 lg:grid-cols-[minmax(0,1fr)_220px]"
                      data-testid={`review-queue-item-${item.id}`}
                    >
                      <div className="min-w-0">
                        <div className="mb-2 flex flex-wrap items-start gap-3">
                          <Checkbox
                            id={`review-queue-${item.id}`}
                            checked={checked}
                            onChange={() => toggleReviewSelection(item.id)}
                            label={item.email ?? 'No email'}
                            description={`${item.company_name ?? 'Unknown company'}${item.position ? ` • ${item.position}` : ''}`}
                          />
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge tone="gray">
                              Score {item.score !== null ? item.score.toFixed(2) : '-'}
                            </Badge>
                            <Badge tone="blue">Queued {formatDateTime(item.created_at)}</Badge>
                          </div>
                        </div>
                        <div className="rounded border border-gray-100 bg-white px-3 py-2 text-sm text-gray-600">
                          {summarizeReasons(item.reasons)}
                        </div>
                      </div>

                      <div className="flex flex-col items-stretch justify-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={reviewResolveMutation.isPending || !canApproveToProvider}
                          onClick={() => resolveReviewQueue('send', [item.id], true)}
                          data-testid={`review-queue-approve-${item.id}`}
                        >
                          <Send size={14} />
                          Approve & queue
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={reviewResolveMutation.isPending}
                          onClick={() => resolveReviewQueue('reject', [item.id], false)}
                          data-testid={`review-queue-reject-${item.id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              Reviews stay here until an operator resolves them into send or reject.
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                disabled={reviewPage === 0}
                onClick={() => setReviewPage((current) => Math.max(0, current - 1))}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                disabled={queueEnd >= (reviewQueueQuery.data?.total ?? 0)}
                onClick={() => setReviewPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
